import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useAppSelector } from '../store/hooks';
import { useParams } from 'react-router-dom';
import { ArticleItem } from '../types/typedeclaration';
import LoadmorenewsBox from '../components/LoadmorenewsBox';
import fetchData from '../Utilis/request';
import HorizontalTabs from '../components/HorizontalTabs';
import { incrementCategory, decrementCategory } from '../store/navigationSlice';
import { useDispatch } from 'react-redux';
import {Loader} from 'components/Index';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const StateToggle = () => {
  const dispatch = useDispatch();
  const settingLocalData = useAppSelector((state:any) => state.staticSettingSlice.data);
  const currentActiveMenu = useAppSelector((state) => state.categoryNavigation.activecategory);
// console.log(settingLocalData, "settingLocalData end ")

  const { stateId: urlStateId } = useParams<{ stateId?: string }>();
  

  const newsContainerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [articles, setArticles] = useState<ArticleItem[]>([]);

  const offsetRef = useRef(0);
  const hasMoreRef = useRef(true);
  const [error, setError] = useState<string | null>(null);

  const [inneritem, setInneritem] = useState<{ [key: string]: string }>({});

  // Flag to track if the initial data fetch has happened
  const isInitialFetchDoneRef = useRef(false);

  // Step 1: Populate inneritem (slug-to-title map)
  useEffect(() => {
    const collectedInnerSlugs: string[] = [];
    const collectedInnertitles: string[] = [];

    settingLocalData?.menu?.forEach((item: any) => {
      if (item?.submenu && item?.id === currentActiveMenu) {
        item?.submenu.forEach((menu: any) => {
          if (menu?.id) {
            collectedInnerSlugs.push(menu?.id);
            collectedInnertitles.push(menu?.title);
          }
        });
      }
    });

    const resultObject = Object.fromEntries(
      collectedInnerSlugs.map((key, index) => [key, collectedInnertitles[index]])
    );
    setInneritem(resultObject);

  }, [currentActiveMenu, settingLocalData]);

  // Extract keys from inneritem to use as the list of tabs (slugs)
  const statelist = Object.keys(inneritem);

  const darkMode = useAppSelector((state) => state.categoryNavigation.darkmode);
  const fontSize = useAppSelector((state) => state.categoryNavigation.fontsize);

  // State for the active tab (which is a slug)
  const [activeTab, setActiveTab] = useState<string>('');

  // Step 2: Set initial activeTab AND trigger first data fetch here
  useEffect(() => {
    // Only proceed if statelist is ready and we haven't set activeTab yet
    if (statelist.length > 0 && !activeTab) {
      const initialTab = urlStateId && statelist.includes(urlStateId) ? urlStateId : statelist[0];
      // Set activeTab
      setActiveTab(initialTab);
    }
  }, [statelist, urlStateId, activeTab]); // Added activeTab to dependency array for stricter checks

  // --- Start: Swipe Detection for Tabs ---
  const touchStartXRef = useRef(0);
  const touchEndXRef = useRef(0);
  const minSwipeDistance = 50; // Minimum pixels for a swipe to be recognized

  const handleTouchStartTabSwipe = useCallback((e: React.TouchEvent<HTMLElement>) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchEndXRef.current = 0; // Reset end position
  }, []);

  const handleTouchMoveTabSwipe = useCallback((e: React.TouchEvent<HTMLElement>) => {
    touchEndXRef.current = e.touches[0].clientX;
  }, []);

  const handleTouchEndTabSwipe = useCallback(() => {
    if (touchEndXRef.current === 0) return; // No move detected
    if (statelist.length === 0) {
      alert("No tabs available to swipe."); // Alert if no tabs exist
      return;
    }

    const distance = touchStartXRef.current - touchEndXRef.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    const currentIndex = statelist.indexOf(activeTab);

    let newActiveTabIndex = currentIndex;

    if (isLeftSwipe) { // Swiped left -> move to next tab (right)
      if (currentIndex < statelist.length - 1) {
        newActiveTabIndex = currentIndex + 1;
      } else {
        dispatch(incrementCategory());
      }
    } else if (isRightSwipe) { // Swiped right -> move to previous tab (left)
      if (currentIndex > 0) {
        newActiveTabIndex = currentIndex - 1;
      } else {
        dispatch(decrementCategory());
      }
    }

    // Only update if the tab actually changed
    if (newActiveTabIndex !== currentIndex) {
      const newActiveTabSlug = statelist[newActiveTabIndex];
      setActiveTab(newActiveTabSlug);
      // Uncomment the line below if you want the URL to update on swipe
      // navigate(`/state/${newActiveTabSlug}`, { replace: true });
    }

    // Reset touch positions
    touchStartXRef.current = 0;
    touchEndXRef.current = 0;
  }, [activeTab, statelist, dispatch]); // Added dispatch to dependency array for clarity
  // --- End: Swipe Detection for Tabs ---


  // Step 3: Fetch articles when activeTab changes
  useEffect(() => {
    if (!activeTab) {
      return;
    }

    // This ensures data is only fetched once per activeTab change,
    // even in React Strict Mode which might double-render effects.
    // However, for the very first fetch on mount, activeTab changes from '' to a value,
    // which triggers this. The `isInitialFetchDoneRef` was an attempt to mitigate,
    // but a cleaner reset before fetch is usually sufficient.
    // console.log("Active tab changed or component mounted with activeTab, fetching new data for:", activeTab);

    // Reset data and fetch for the new active tab
    setArticles([]);
    offsetRef.current = 0;
    hasMoreRef.current = true;
    // Set loading immediately before the async call
    setLoading(true);
    setError(null);

    const fetchInitialData = async () => {
      try {
        const url = `${API_BASE_URL}getArticleByCat.php?slug=${activeTab}&start=0`;
        const articleData = await fetchData.getResult(url);

        const items = articleData?.articles || [];
        setArticles(items);
        offsetRef.current = items.length; // Correctly set offset
        hasMoreRef.current = articleData?.status !== false && items.length > 0;

      } catch (err) {
        console.error(`Error fetching articles for ${activeTab}:`, err);
        setError(err instanceof Error ? err.message : `Failed to load articles for ${activeTab}`);
      } finally {
        setLoading(false); // Ensure loading is false even on error
      }
    };

    fetchInitialData();

  }, [activeTab]); // Depend on activeTab.

  // Fetch more articles (infinite scroll)
  const fetchMoreArticles = useCallback(async () => {
    // Crucial check: if already loading, or no more data, or no active tab, return
    if (loading || !hasMoreRef.current || !activeTab) {
      return;
    }

    setLoading(true); // Set loading to true immediately
    setError(null); // Clear previous errors

    try {
      const url = `${API_BASE_URL}getArticleByCat.php?slug=${activeTab}&start=${offsetRef.current}`;
      const articleData = await fetchData.getResult(url);
      const newItems = articleData?.articles || [];

      setArticles(prev => [...prev, ...newItems]);
      offsetRef.current += newItems.length;
      hasMoreRef.current = articleData?.status !== false && newItems.length > 0;

    } catch (err) {    
      setError(err instanceof Error ? err.message : 'Failed to load more results');
    } finally {
      setLoading(false); // Ensure loading is false
    }
  }, [activeTab, loading]); // `loading` is a dependency here to ensure the latest `loading` state is captured.

  const handleScroll = useCallback(() => {
    const container = newsContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    // Trigger fetch when user is 300px from the bottom
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 40;

    // Only call fetchMoreArticles if we are near the bottom AND not already loading
    // This is the critical change to prevent multiple calls
    if (isNearBottom && !loading) {
      fetchMoreArticles();
    }
  }, [fetchMoreArticles, loading]); // Added 'loading' to the dependency array

  // Attach scroll event listener
  useEffect(() => {
    const container = newsContainerRef.current;
    if (!container) return;

    // Ensure the event listener is added only once
    container.addEventListener('scroll', handleScroll);

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]); // `handleScroll` is stable due to useCallback

  // Get category title for display
  const categorytitle = inneritem[activeTab] || activeTab || 'Category';

  return (
    <div className={`font-sans min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'}`}>
      {/* Horizontal Tabs Navigation */}
      <div className=" left-0 right-0 z-60 HorizontalTabs pt-[100px]">
        <HorizontalTabs
          tabs={statelist}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          darkMode={darkMode}
          tabsTitleList={inneritem}
        />
      </div>

      <main
        className="pt-[10px] "
        ref={newsContainerRef}
        // Removed onScroll directly from JSX as it's handled by useEffect to prevent re-attaching
        style={{ height: 'calc(100vh - 50px)', overflowY: 'auto' }}
        onTouchStart={handleTouchStartTabSwipe}
        onTouchMove={handleTouchMoveTabSwipe}
        onTouchEnd={handleTouchEndTabSwipe}
      >

        <div className="news-container">
          {error && (
            <div className={`text-center py-4 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
              {error}
            </div>
          )}

          {articles.length > 0 && (
            <LoadmorenewsBox
              darkMode={darkMode}
              fontSize={fontSize}
              articles={articles}
              inlineView={true}
              title={categorytitle}
            />
          )}

          {loading && (<Loader darkMode={darkMode}/>)}

          {!hasMoreRef.current && articles.length > 0 && (
            <div className={`text-center pb-16 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              No more articles to load
            </div>
          )}

          {!loading && articles.length === 0 && !error && (
            <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <Loader darkMode={darkMode}/> 
              {/* {activeTab} */}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default StateToggle;